import dayjs from 'dayjs';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Divider, Flex, HoverCard, Indicator, Skeleton, Text } from '@mantine/core';
import { DatePickerProps, Calendar as MantineCalendar } from '@mantine/dates';

import { useQuery } from '@tanstack/react-query';

import { AppRoutes } from '@/router/routes';

import { bookingApi } from '@/shared/api/booking';
import { isActiveBooking, isFutureBooking, resetTime } from '@/shared/helpers/utils';
import useBookingStore from '@/shared/store/booking.store';
import { PersonBookingsObject } from '@/shared/types/booking.types.ts';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

type BookingOnDate = {
	time: number;
} & PersonBookingsObject;

const getDayProps = (date: Date, onClick: () => void) => {
	if (dayjs(date).isToday()) {
		return {
			style: {
				backgroundColor: 'var(--mantine-primary-color-0)',
				color: '#ffffff',
			},
			onClick: onClick,
		};
	}

	return {
		onClick: onClick,
	};
};

export const Calendar = () => {
	const { data: bookings, isLoading: isLoadingBookings } = useQuery({
		queryKey: ['bookings'],
		queryFn: bookingApi.getBookingsForClient,
	});

	const navigate = useNavigate();

	const { setBookingDate } = useBookingStore();

	const [isActiveOrFuture, setIsActiveOrFuture] = useState<BookingOnDate[]>();

	const handleDayClick = (date: Date) => {
		const newDate = dayjs(date).isBefore(dayjs(), 'day') ? dayjs() : dayjs(date);

		setBookingDate([newDate.toDate(), newDate.toDate()]);

		navigate(AppRoutes.BookingNew);
	};

	const dayRenderer: DatePickerProps['renderDay'] = (date) => {
		const day = date.getDate();
		const booking = isActiveOrFuture?.filter((elem) => elem.time === dayjs(date).unix()) ?? [];

		return (
			<HoverCard width={'fit-content'} withArrow shadow="md">
				<HoverCard.Target>
					<Indicator
						position="bottom-center"
						size={booking.length > 1 ? 10 : 6}
						offset={-2}
						disabled={!booking.length}
						label={
							booking.length > 1 ? (
								<Text size={'xs'} fw={700}>
									{booking.length}
								</Text>
							) : (
								''
							)
						}
						autoContrast
						{...(dayjs(date).isToday() ? { color: '#ffffff' } : {})}
					>
						<div>{day}</div>
					</Indicator>
				</HoverCard.Target>

				{booking.length > 0 && (
					<HoverCard.Dropdown onClick={(event) => event.stopPropagation()}>
						{booking.map((b, idx) => (
							<Fragment key={b.id}>
								<Text size="md" fw={600}>
									{b.booking_object.name}
								</Text>
								{idx !== booking.length - 1 && <Divider />}
							</Fragment>
						))}
					</HoverCard.Dropdown>
				)}
			</HoverCard>
		);
	};

	const getIsActiveOrFuture = useCallback(() => {
		const filtered = bookings
			?.filter((item) => isActiveBooking(item.start, item.end) || isFutureBooking(item.start))
			.map((i) => ({
				...i,
				time: resetTime(i.start).unix(),
			}));

		setIsActiveOrFuture(filtered);
	}, [bookings]);

	useEffect(() => {
		getIsActiveOrFuture();
	}, [bookings]);

	return (
		<Skeleton radius={24} visible={isLoadingBookings}>
			<LayoutRow type="once">
				<Flex align="center" justify="center" style={{ overflow: 'hidden' }}>
					<MantineCalendar
						maxLevel={'month'}
						size="md"
						styles={{
							day: {
								borderRadius: 50,
							},
						}}
						renderDay={dayRenderer}
						getDayProps={(date) => getDayProps(date, () => handleDayClick(date))}
					/>
				</Flex>
			</LayoutRow>
		</Skeleton>
	);
};
